/* eslint-disable react/destructuring-assignment */
import { Column } from "@ant-design/plots";
import { Empty, Skeleton } from "antd";
import React, { memo, useMemo } from "react";

// Tooltip color tokens from the dashboard design system.
const TT_LABEL_CLS = "text-black-65 text-xs font-normal";
const TT_VALUE_CLS = "text-black-88 text-xs font-normal";

// Distinct dot colors for each row in the Messages Sent vs. Received tooltip,
// matching the Figma palette.
const TT_DOT_COLORS = {
  OTTO: "#6395F9", // blue
  AI_ASSISTED: "#EC4899", // pink — clearly distinct from mint Received
  MANUAL: "#F0884D", // orange
  RECEIVED: "#5AD8A6", // mint green
  ENGAGEMENT: "#A855F7", // purple
};

// Series → bar color map for the grouped+stacked Messages Sent vs. Received
// chart. Keys must match the `name` field on each chart row.
const MESSAGE_SERIES_COLORS = {
  Otto: TT_DOT_COLORS.OTTO,
  "AI-assisted": TT_DOT_COLORS.AI_ASSISTED,
  Manual: TT_DOT_COLORS.MANUAL,
  Received: TT_DOT_COLORS.RECEIVED,
  Engagement: TT_DOT_COLORS.ENGAGEMENT,
};

// Series → friendly label.
const SERIES_LABELS = {
  sentCount: "Total sent",
  receivedCount: "Received",
  engagementCount: "Engagement",
};

const escapeHtml = str =>
  String(str ?? "").replace(
    /[&<>"']/g,
    ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch])
  );

const dot = color =>
  `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:8px;flex-shrink:0;"></span>`;

const ttRow = (label, value, color, indent = false) => `
  <div class="flex items-center justify-between ${indent ? "pl-5" : ""} py-0.5">
    <span class="flex items-center ${TT_LABEL_CLS}">
      ${color ? dot(color) : ""}${escapeHtml(label)}
    </span>
    <span class="${TT_VALUE_CLS} ml-6">${escapeHtml(value)}</span>
  </div>`;

/**
 * Factory that builds the "Messages Sent vs. Received" tooltip renderer.
 * Closes over a per-user lookup built from the chart's own `graphData` so the
 * tooltip pulls authoritative numbers regardless of what G2 chooses to put
 * in `items[i].data` (which is unreliable for group+stack columns — sidecar
 * fields can be stripped and segment names aren't always surfaced).
 *
 * Renders:
 *   <user name>
 *   ● Total sent:   <total>
 *     ● Otto         <n>
 *     ● AI-assisted  <n>
 *     ● Manual       <n>
 *   ● Received:     <n>
 *   ● Engagement:   <n>
 */
const makeMessageTooltip =
  userLookup =>
  (title, items = []) => {
    if (!items.length) return "";
    // `title` is the xField value (userFullName) provided by G2 for the
    // hovered x position — always a string regardless of how `items[].data`
    // is shaped. Falling back to items[0].data.userFullName guards against
    // future G2 changes.
    const userName = title || items[0]?.data?.userFullName || "";
    const u = userLookup[userName] || {};
    const sent = Number(u.totalSent || 0);
    const otto = Number(u.otto || 0);
    const aiAssisted = Number(u.aiAssisted || 0);
    const manual = Number(u.manual || 0);
    const received = Number(u.received || 0);
    const engagement = Number(u.engagement || 0);

    return `
    <div class="px-2 py-2 min-w-[220px]">
      <div class="text-sm font-semibold text-black-88 mb-2">${escapeHtml(userName)}</div>
      ${ttRow("Total sent:", sent, null)}
      ${ttRow("Otto", otto, TT_DOT_COLORS.OTTO, true)}
      ${ttRow("AI-assisted", aiAssisted, TT_DOT_COLORS.AI_ASSISTED, true)}
      ${ttRow("Manual", manual, TT_DOT_COLORS.MANUAL, true)}
      ${ttRow("Received:", received, TT_DOT_COLORS.RECEIVED)}
      ${ttRow("Engagement:", engagement, TT_DOT_COLORS.ENGAGEMENT)}
    </div>`;
  };

const BarChart = props => {
  const { graphData, type, xField, yField, seriesField, groupField, colors, graphKey } = props;
  const isMessageSentReceived = graphKey === "messageSentRecieved";

  // Per-user authoritative numbers for the message tooltip. Built from the
  // raw chart rows (the sidecar fields are identical on every row of a user,
  // so picking the first hit is safe). Used in place of `items[i].data`
  // which G2 strips inconsistently in group+stack mode.
  const messageUserLookup = useMemo(() => {
    if (!isMessageSentReceived) return null;
    return (graphData || []).reduce((map, row) => {
      if (row && row.userFullName && !map[row.userFullName]) map[row.userFullName] = row;
      return map;
    }, {});
  }, [isMessageSentReceived, graphData]);

  const config = {
    data: graphData,
    xField: xField || "type",
    yField: yField || "value",
    seriesField: seriesField || "type",
    // Group+stack only for the messageSentRecieved chart — the Sent bar is
    // stacked (Otto + AI-assisted + Manual) while Received and Engagement
    // sit as separate single-segment grouped bars. Series color is keyed by
    // the row's `name` so each segment paints the right hue.
    ...(isMessageSentReceived && groupField
      ? {
          isGroup: true,
          isStack: true,
          groupField,
          color: ({ [seriesField || "name"]: seriesName }) =>
            MESSAGE_SERIES_COLORS[seriesName] || "#999",
        }
      : {
          isGroup: type === "GROUP",
          isStack: type === "STACK",
          color: colors,
        }),
    xAxis: {
      label: {
        formatter: text => {
          return text
            .split(" ")
            .map(word => word[0])
            .join("");
        },
        // autoHide: false,
      },
    },
    height: 320,
    autoFit: true,
    legend: {
      position: "bottom",
      flipPage: false,
    },
    maxColumnWidth: props.width || 8,
    interactions: [
      {
        type: "element-active",
      },
    ],
    columnStyle: {
      radius: [20, 20, 20, 20],
    },
    label: false,
    ...(isMessageSentReceived && {
      tooltip: {
        shared: true,
        showMarkers: false,
        customContent: makeMessageTooltip(messageUserLookup || {}),
        // Per-chart GPU acceleration hints — promotes this tooltip to a
        // composite layer so rapid cursor moves don't jitter. Scoped here
        // via `domStyles` so other charts aren't affected.
        domStyles: {
          "g2-tooltip": {
            willChange: "top, left, transform, opacity",
            backfaceVisibility: "hidden",
          },
        },
      },
    }),
    ...(!isMessageSentReceived && {
      tooltip: {
        formatter: datum => ({
          name: SERIES_LABELS[datum.name] || datum.name,
          value: datum.value,
        }),
      },
    }),
  };

  return (
    <Skeleton className="h-[320px]" avatar loading={props.loader} active>
      {graphData?.length > 0 ? (
        <Column className="flex-1" {...config} />
      ) : (
        <Empty className="h-[320px] flex flex-col justify-center" />
      )}
    </Skeleton>
  );
};

export default memo(BarChart); // converted into pure component to prevent unwanted rendering
